import { useInViewport } from "@mantine/hooks";
import { ChevronDownIcon, SearchIcon } from "lucide-react";
import { useCallback, useRef } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useI18nContext } from "~/i18n/i18n-react";
import type { RouterOutputs } from "~/utils/api";
import { cn } from "~/utils/cn";
import { Button } from "../ui/button";
import { Form, FormField } from "../ui/form";
import { Input } from "../ui/input";
import { PageLoader } from "../ui/page-loader";
import { ScrollArea } from "../ui/scroll-area";
import { RoomCard } from "./room-card";

interface Props {
	rooms?: RouterOutputs["room"]["list"]["rooms"];
	isLoading: boolean;
}

import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "../ui/table";

export const RoomList = ({ rooms, isLoading }: Props) => {
	const { LL } = useI18nContext();
	const scrollAreaRef = useRef<HTMLDivElement>(null);
	const { ref: lastRoomRef, inViewport: lastRoomInViewport } = useInViewport();

	if (isLoading) {
		return (
			<div>
				<PageLoader className="h-96" />
			</div>
		);
	}
	if (!rooms?.length) {
		return (
			<div className="flex h-full w-full flex-1 items-center justify-center md:flex-auto">
				<p className="font-extralight italic">{LL.NO_ROOMS_LIST()}</p>
			</div>
		);
	}
	return (
		<div className="relative flex w-full max-w-lg flex-1 flex-col items-center justify-center md:flex-auto">
			<ScrollArea
				className={cn(
					"h-[16rem] max-h-[16rem] w-full pr-4 md:h-[22rem] md:max-h-[22rem] xl:max-h-[32rem] 2xl:h-[32rem]",
					{
						"md:h-20": !rooms.length,
					}
				)}
				ref={scrollAreaRef}
			>
				<Table>
					<TableHeader>
						<TableRow>
							<TableHead>{LL.ROOM()}</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{rooms.map((room, index) => (
							<TableRow
								key={room.id}
								ref={index === rooms.length - 1 ? lastRoomRef : undefined}
							>
								<TableCell>
									<RoomCard room={room} roomId={room.id} />
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</ScrollArea>
			{!lastRoomInViewport && (
				<div className="pointer-events-none absolute bottom-0 left-1/2 flex -translate-x-1/2 transform items-center justify-center bg-gradient-to-t from-background via-background/80 to-transparent pt-4 pb-2">
					<ChevronDownIcon className="h-5 w-5 animate-bounce text-muted-foreground" />
				</div>
			)}
		</div>
	);
};

interface SearchFormInputs {
	search: string;
}
interface SearchBarProps {
	onSearch: SubmitHandler<SearchFormInputs>;
}

import { useDebouncedCallback } from "@mantine/hooks";

export const SearchBar = ({ onSearch }: SearchBarProps) => {
	const form = useForm<SearchFormInputs>();

	const { LL } = useI18nContext();

	const debouncedSearch = useDebouncedCallback(
		(value: string) => onSearch({ search: value }),
		{
			delay: 300,
		}
	);

	const handleChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const value = e.target.value;
			form.setValue("search", value);
			debouncedSearch(value);
		},
		[form, debouncedSearch]
	);

	return (
		<Form {...form}>
			<form
				className="flex w-full gap-2 md:pr-3"
				onSubmit={(e) => e.preventDefault()}
			>
				<FormField
					name="search"
					render={({ field }) => (
						<Input
							{...field}
							aria-label="Search for room"
							className="w-full"
							onChange={handleChange}
							placeholder={LL.SEARCH_FOR_A_ROOM()}
							type="text"
						/>
					)}
				/>
				<Button aria-label="Search button" type="submit">
					<SearchIcon className="h-6 w-6" />
				</Button>
			</form>
		</Form>
	);
};
